import { Box, HStack, VStack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { formatCurrency, formatAmount } from '@/lib/utils/format';
import type { UserNonFundingLedgerUpdate } from '@nktkas/hyperliquid';

interface LedgerHistoryCardProps {
  ledgerUpdate: UserNonFundingLedgerUpdate;
}

export function LedgerHistoryCard({ ledgerUpdate }: LedgerHistoryCardProps) {
  const t = useTranslations('trader.ledgerHistory');

  // Extract ledger details
  const timestamp = new Date(ledgerUpdate.time);
  const date = timestamp.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const time = timestamp.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Determine operation type and amount based on delta type
  const getOperationInfo = () => {
    const delta = ledgerUpdate.delta;

    switch (delta.type) {
      case 'deposit':
        return {
          operation: t('deposit'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
          isPositive: true,
        };
      case 'withdraw':
        return {
          operation: t('withdraw'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
          isPositive: false,
        };
      case 'accountClassTransfer':
        return {
          operation: t('accountTransfer'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
          isPositive: delta.toPerp,
        };
      case 'internalTransfer':
        return {
          operation: t('internalTransfer'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
        };
      case 'spotTransfer':
        return {
          operation: t('spotTransfer'),
          amount: formatAmount(delta.usdcValue),
          currency: 'USDC',
        };
      case 'subAccountTransfer':
        return {
          operation: t('subAccountTransfer'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
        };
      case 'liquidation':
        return {
          operation: t('liquidation'),
          amount: formatAmount(delta.liquidatedNtlPos),
          currency: 'USDC',
          isPositive: false,
        };
      case 'rewardsClaim':
        return {
          operation: t('rewardsClaim'),
          amount: formatAmount(delta.amount),
          currency: 'USDC',
          isPositive: true,
        };
      case 'vaultCreate':
        return {
          operation: t('vaultCreate'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
          isPositive: false,
        };
      case 'vaultDeposit':
        return {
          operation: t('vaultDeposit'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
          isPositive: false,
        };
      case 'vaultWithdraw':
        return {
          operation: t('vaultWithdraw'),
          amount: formatAmount(delta.requestedUsd),
          currency: 'USDC',
          isPositive: true,
        };
      case 'vaultDistribution':
        return {
          operation: t('vaultDistribution'),
          amount: formatAmount(delta.usdc),
          currency: 'USDC',
          isPositive: true,
        };
      default:
        return {
          operation: (delta as any).type,
          amount: formatAmount((delta as any).usdc),
          currency: 'USDC',
        };
    }
  };

  const operationInfo = getOperationInfo();

  return (
    <Box
      bg="white/5"
      border="1px solid"
      borderColor="white/8"
      borderRadius="xl"
      p={4}
      width="full"
    >
      <HStack justify="space-between" align="flex-end" gap={2} w="full">
        {/* Left: Date, Time and Operation Type */}
        <VStack align="start" gap={1} flex={1}>
          <HStack gap={2} align="center">
            <Text fontSize="12px" fontWeight="medium" color="white/40">
              {date}
            </Text>
            <Text fontSize="12px" fontWeight="medium" color="white/40">
              {time}
            </Text>
          </HStack>
          <Text
            fontSize="14px"
            fontWeight="semibold"
            color="white/90"
            textAlign="left"
          >
            {operationInfo.operation}
          </Text>
        </VStack>

        {/* Right: Amount and Currency */}
        <VStack align="end" gap={1}>
          <HStack gap={2} align="center">
            <Text
              fontSize="14px"
              fontWeight="semibold"
              color={
                operationInfo.isPositive
                  ? 'profit'
                  : operationInfo?.isPositive === false
                    ? 'loss'
                    : 'text.t1/90'
              }
            >
              {operationInfo.isPositive
                ? '+'
                : operationInfo?.isPositive === false
                  ? '-'
                  : ''}
              {operationInfo.amount}
            </Text>
            <Text
              fontSize="14px"
              fontWeight="semibold"
              color={
                operationInfo.isPositive
                  ? 'profit'
                  : operationInfo?.isPositive === false
                    ? 'loss'
                    : 'text.t1/90'
              }
            >
              {operationInfo.currency}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
}
